import {Avatar, MantineColor, Tooltip, useSafeMantineTheme} from '@mantine/core'
import {scaleOrdinal} from 'd3-scale'
import {isEmpty, range, uniqueBy} from 'remeda'
import {O} from 'ts-toolbelt'
import type {Profile, ProfileUI} from './profileTypes'
import type {ReactNode} from 'react'
import {Tip} from 'shared/ui'

const initials = (name: string) =>
  name
    .split(/[\s\.]+/)
    .map(n => n[0]?.toUpperCase())
    .join('')

export type AvatarProfile = O.Optional<Pick<Profile, 'avatar_url' | 'id' | 'name'>, 'id' | 'name' | 'avatar_url'>
export function SAvatar<C = 'div'>({
  radius = 'md',
  size = 'md',
  profile,
  profiles,
  src: _src,
  styles,
  component,
  tip,
  hideTip,
  ...p
}: React.ComponentProps<typeof Avatar<C>> & {
  profile?: AvatarProfile
  profiles?: AvatarProfile[]
  tip?: ReactNode
  hideTip?: boolean
}) {
  const color = profileColor(profile)
  const src = _src ?? profile?.avatar_url
  const {name} = profile ?? {}
  const hasProfile = !!profile
  return (
    <Tip
      classNames={{tooltip: 'sensitive'}}
      label={hasProfile ? (tip ?? name) : 'User profile information might be out of date. Please refresh the page.'}
      withinPortal
      disabled={hideTip}>
      <span>
        <Avatar
          component={component as any}
          imageProps={{referrerPolicy: 'no-referrer'}}
          styles={
            component == 'span' ?
              {
                ...styles,
                root: {display: 'inline-block', verticalAlign: 'middle'},
                placeholder: {display: 'inline-block'}
              }
            : styles
          }
          color={color as any}
          {...{...p, radius, size, src}}>
          {hasProfile ? initials(name ?? '') : null}
        </Avatar>
      </span>
    </Tip>
  )
}

export const SAvatarGroup = ({profiles, className}: {profiles: ProfileUI[]; className?: string}) =>
  isEmpty(profiles) ?
    <></>
  : <Avatar.Group spacing='xs' className={className}>
      {uniqueBy(profiles, p => p.id).map(p => (
        <Tooltip key={p.id} label={p.name} withArrow>
          <Avatar src={p.avatar_url} color={profileColor(p)} radius='xl' size='sm'>
            {initials(p.name)}
          </Avatar>
        </Tooltip>
      ))}
    </Avatar.Group>

const profileColor = (p?: AvatarProfile) => hashToColor(p?.id ?? p?.name ?? '') as MantineColor

const hashCode = (s: string) => s.split('').reduce((h, c) => (h = c.charCodeAt(0) + (h << 6) + (h << 16) - h), 0)
const avatarColors = [
  'amber',
  'blue',
  'teal',
  'yellow',
  'gray',
  'green',
  'indigo',
  'rose',
  'purple',
  'orange'
] satisfies MantineColor[]
const colorScale = scaleOrdinal(range(0, avatarColors.length - 1), avatarColors)
const hashToColor = (s: string) => colorScale(Math.abs(hashCode(s)) % avatarColors.length)
